import { Component, OnDestroy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faPrint, faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { AllowDragging, CellEditEndingEventArgs, FlexGrid } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'
import { QuillViewComponent } from 'ngx-quill'

import { BaseComponent, BusinessPlanSection, FeatureType, Grid, ModalSize } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { BusinessPlanSectionComponent } from './business-plan-section.component'
import { BusinessPlanSummaryComponent } from './business-plan-summary.component'

@Component({
    selector: 'app-business-plan-sections',
    templateUrl: './business-plan-sections.component.html',
    styleUrls: ['./business-plan-sections.component.css'],
    standalone: true,
    imports: [
        FaIconComponent,
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        QuillViewComponent,
        BusinessPlanSummaryComponent,
    ],
})
export class BusinessPlanSectionsComponent extends BaseComponent implements OnDestroy {
    faSearch = faSearch
    faPrint = faPrint

    canViewAdmin = true
    activeTab = 1

    grid: Grid<BusinessPlanSection>

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.UsersView])
    }

    constructor() {
        super()

        this.grid = new Grid<BusinessPlanSection>(this.services)
            .setupModalEditing(
                this.services.data.businessPlanSections,
                'Business Plan Section',
                BusinessPlanSectionComponent,
                () => new BusinessPlanSection(),
                this,
                ModalSize.medium,
            )
            .filterBy(item => [item.heading, item.theme])
            .isReadOnly(false)
            .allowDragging(AllowDragging.Both)

        this.grid.itemAdded.on(this.handleItemAdded.bind(this))

        const reordered = () => {
            this.gridReordered()
        }
        this.grid.dragged.on(reordered)

        this.refreshBusinessPlanSections()
    }

    ngOnDestroy(): void {
        this.grid.itemAdded.off(this.handleItemAdded.bind(this))
        this.grid.dragged.off(this.gridReordered.bind(this))
    }

    handleItemAdded() {
        this.refreshBusinessPlanSections()
    }

    refreshBusinessPlanSections() {
        this.cancelableRequest(
            'BusinessPlanSectionsRefresh',
            true,
            this.services.data.businessPlanSections.queryAll().subscribe({
                next: result => {
                    this.initialLoadingComplete()
                    this.grid.items = result
                },
            }),
        )
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        this.updateRecords([flexGrid.rows[args.row].dataItem])
    }

    updateRecords(items: any[]) {
        this.services.data.businessPlanSections.edit(items[0]).subscribe({
            next: () => {
                this.grid.refresh()
            },
            error: () => {
                this.services.notification.error('The business plan section changes were not successfully saved')
            },
        })
    }

    gridReordered() {
        const sections = this.grid.view.items.slice()
        this.grid.items = this.grid.view.items.slice() as any
        this.cancelableRequest('reorderSections', true, this.services.data.put('/api/BusinessPlanSection/BulkUpdate', sections).subscribe())
    }

    tabChanged() {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 50)
    }

    printDocument() {
        window.print()
    }
}
